/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosInstance} from 'axios';
import {GenericObject} from '../types/entities';
import {AxiosConfig, HEREConfig} from '../types/services';
import {createAsyncClient} from './AsyncClientManager';

/**
 * Defines the static, build-time configuration Here Maps API key value.
 */
export const getHereAPIKey = (): string => process.env.NEXT_PUBLIC_HERE_API_KEY;

/**
* Defines a refernece to the configured Axios HTTP client instance.
*/
export let client: AxiosInstance;

/**
* Defines a reference to the CTC Configuration which is used by all requests.
*/
export let config: AxiosConfig;

/**
 *
 * The parseEntity utility allows for parsing a HERE response which returns
 * an Array containing a single entity.
 *
 * ```typescript
 *
 * // default case
 *    export const createAlignedNodes = response => {
 *      const { waypoint } = parse(response, 'waypoint');
 *      ...
 *    }
 *
 *
 * ```
 */
export const parse = (resp: any, entity: string): any => {
  let route = resp?.data?.response?.route;
  if (route?.length) {
    route = route[0];
    if (entity === 'link') {
      return route?.leg?.[0][entity];
    }
  }
  return route[entity];
};

/**
 * createAlignedNodes function creates an array of aligned nodes
 * from the HERE API calculateRoute response
 * The returning values will be used to create nodes on the map
 * In case of node being algined beyond MAX_DISTANCE, initial coordinate
 * from assets will be used for the returning node.
 *
 * ```typescript
 *
 * import { createAlignedNodes } from '@ion/core/src/HereUtils';
 *
 * const alignedNodes = createAlignedNodes(response);
 * ...
 *
 * ```
 */
export const createAlignedNodes = (response, assets, MAX_DISTANCE) => {
  const waypoint     = parse(response, 'waypoint');
  const alignedNodes = waypoint.map((elem, index) => {
    const {longitude, latitude} = elem.mappedPosition;
    const routeLinkSeqNrMatched = elem.routeLinkSeqNrMatched;
    // edge case where algined node is further than MAX_DISTANCE meters away from the original node
    if (elem.matchDistance > MAX_DISTANCE) {
      return {
        ...assets[index],
        routeLinkSeqNrMatched
      };
    }

    return {
      ...assets[index],
      routeLinkSeqNrMatched,
      latitude,
      longitude
    };
  });
  return alignedNodes;
};

/**
 *
 * createPathArray function creates an array of route nodes
 * from the HERE API calculateRoute response
 *
 * ```typescript
 *
 * import { createRouteArray } from '@ion/core/src/HereUtils';
 *
 * const routeArray = createRouteArray(response);
 * ...
 *
 * ```
 *
 */
export const createPathArray = (response: object, alignedNodes: Array<any>): Array<any> => {
  const link              = parse(response, 'link');
  let lastMatchingIndex = 0;
  // setting the first matching event time for the paths as event time of second node
  // this ensures that paths will be shown only when the node is at the next point.
  let matchingEventTime;
  for (let i = 0; i < alignedNodes.length; i++) {
    // get new matching Index from corresponding alignNode
    const matchingIndex = alignedNodes[i].routeLinkSeqNrMatched;
    // if current matching index !== last matching index
    if (matchingIndex !== lastMatchingIndex) {
      // update the matchignEventTime to next event time.
      matchingEventTime = alignedNodes[i].eventTime;
      // update eventTime of corresponding routeArray
      for (let j = lastMatchingIndex; j < matchingIndex; j++) {
        link[j].OBJECTID  = alignedNodes[i].OBJECTID;
        link[j].eventTime = matchingEventTime;
        link[j].assetId   = alignedNodes[0].assetId;
        link[j].tripGroup = alignedNodes[i].tripGroup;
        addInputs(alignedNodes[i], link[j]);
      }
      // update the last matching index to current
      lastMatchingIndex = matchingIndex;
    }
    // if index is at the last element of alignNodes array
    if (i === alignedNodes.length-1) {
      // update eventTime of corresponding routeArray till the end.
      for (let j = matchingIndex; j < link.length; j++) {
        link[j].OBJECTID  = alignedNodes[i].OBJECTID;
        link[j].eventTime = alignedNodes[i].eventTime;
        link[j].tripGroup = alignedNodes[i].tripGroup;
        link[j].assetId   = alignedNodes[0].assetId;
        addInputs(alignedNodes[i], link[j]);
      }
    }
  }

  const pathArray  = link.map(elem => {
    const {shape}   = elem;
    const arr       = [];
    for (let i = 0; i < shape.length; i += 2) {
      const inputs = addInputs(elem, null, true);
      arr.push({
        OBJECTID  : elem.OBJECTID,
        eventTime : elem.eventTime,
        assetId   : elem.assetId,
        tripGroup : elem.tripGroup,
        latitude  : shape[i],
        longitude : shape[i+1],
        ...inputs
      });
    }
    return arr;
  }).flat();

  return pathArray;
};

/**
 * Utility method to return Inputs or add inputs to a source.
 */
const addInputs = (item: any, source: any, returnObject: boolean = false) => {
  if (item) {
    const inputs = Object.keys(item)
      .filter(key => key.startsWith('input'))
      .reduce((acc, key) => {
        acc[key] = item[key];
        return acc;
      }, {});
    return returnObject ? {...inputs} : Object.assign(source, inputs);
  }
  return {};
};

/**
* It takes array of objects contains longitude and latitude properties
* and returns requestBody in CSV format
*
* ```typescript
*
* import {createCSV} from '@ion/core/src/HereUtils';
*
* const CSV = createCSV(assets : Array<CSVAssetType>);
* ...
*
* ```
*/
export const createCSV = (coordinateArray: Array<any>, headers: string): string => {
  return coordinateArray?.reduce((all, {longitude, latitude, heading}) => (
    `${all}${longitude},${latitude},${heading}\n`
  ), `${headers}\n`);
};

/**
 *
 *
 * The createFleetRequestUrl function create a request URL for
 * FleetService of HERE api
 *
 * ```typescript
 *
 * import {createFleetRequestUrl} from '@ion/core/src/HereUtils';
 *
 * const url = createFleetRequesturl(endpoint, mode, apikey);
 * ...
 *
 * ```
 */
export const createFleetRequestUrl = (endPoint, mode, apiKey) => {
  const routeMatch   = 1;
  return `${endPoint}apiKey=${apiKey}&mode=${mode}&routeMatch=${routeMatch}&ignoreWaypointsFarFromNeighbors=false`;
};

/**
 *
 * The setHereConfig function allows for initializing the configuration
 * of the HERE API, which will be used by all subsequent Here API Requests.
 * The optional `hereApiKey` will only be used (if provided) in the event
 * that the default `process.env.NEXT_PUBLIC_HERE_API_KEY` build-time
 * configuration is not available.
 *
 * ```typescript
 *
 *    import {loadConfig} from '@ion/core/src/environment/environment';
 *    import {setHereConfig} from '@ion/core/src/services/HereUtils';
 *
 *    loadConfig(
 *      config => {
 *        setHereConfig(config.hereFleetServiceURL, config.hereApiConfigs, config.hereApiKey);
 *        ...
 *      },
 *      ...
 *    );
 *
 * ```
 */
export const setHereConfig = ({
  hereFleetServiceURL,
  hereApiKey
}: HEREConfig = {}) => {
  config = {
    withCredentials     : true,
    baseURL             : hereFleetServiceURL,
    hereFleetServiceURL : hereFleetServiceURL,
    hereApiKey          : getHereAPIKey() || hereApiKey,
    timeout             : 60000,
    headers             : {
      'Content-Type' : 'text/plain'
    }
  };
  client = createAsyncClient(config, false);
  return client;
};

/**
 * The normalizeAddressLabel normalizes the address label of HERE response and converts it to
 * as per iOn apps standard. This will just returning the HERE Address label aligment reversely
 * which is the std. of iOn apps.
 *
 * ```typescript
 *
 *  import {normalizeAddressLabel} from '@ion/core/src/services/Here';
 *
 *  const displayLabel = normalizeAddressLabel('India, Chennai',  'City');
 *
 * ```
 */
export const normalizeAddressLabel = (label: string, matchLevel = null) => (
  `${label}${matchLevel ? ` (${matchLevel})` : ''}`.trim()
);

/**
 * The parseSuggestions parse the suggestions by HERE API and returns a list of suggestions
 * as per the modification of iOn apps requirements like address labels change or any others.
 *
 * ```typescript
 *
 *  import {parseSuggestions} from '@ion/core/src/services/Here';
 *
 *  const parsed = parseSuggestions(suggestion, true);
 *
 * ```
 */
export const parseSuggestions = (suggestion: GenericObject, showMatchLevel = false) => ({
  ...suggestion,
  data : {
    ...suggestion.data,
    suggestions : suggestion.data.items?.map?.((locations: GenericObject) => {
      const {title, id, localityType, resultType} = locations;
      return ({
        ...locations,
        locationId : id,
        label      : normalizeAddressLabel(title, showMatchLevel ? (localityType || resultType) : null)
      });
    }).filter(({resultType}) => !resultType || resultType !== 'chainQuery')
  }
});
